import * as React from 'react';

import type { CampaignDetailDto, CycleNumberDto } from '@/api/campaign-client';
import {
  useCloseCycle,
  useOpenCampaign,
  useOpenCampaignC2,
  usePlanCampaign,
} from '@/api/use-campaigns';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { cycleNumberLabel } from '@/lib/campaign-labels';
import { DARK_BUTTON, SideCard } from '@/routes/admin/campaigns/detail-ui';

// =============================================================================
// lifecycle-actions-panel.tsx — Carte « Actions » du détail campagne (transitions d'état disponibles).
// =============================================================================
// GATE_HUMAN 2026-09-16 (Q3) : carte en tête de la colonne droite, rendue SEULEMENT si une action d'état est
// disponible. Extrait de campaign-detail-screen.tsx, gardes d'état et mutations INCHANGÉES :
//   - Brouillon  → « Planifier le paramétrage » (S11 / E-3b-1, POST /plan) ;
//   - Planifiee  → « Lancer la campagne » + confirmation inline (UC-A-046, POST /open) ;
//   - OuverteC1/C2 → « Clôturer le cycle N » + fenêtre de confirmation (EARLY-CLOSE A-15, G0-S15-2) ;
//   - AnalyseC1  → « Ouvrir le Cycle 2 » + fenêtre de confirmation (A-17, G0-S15-4 : masqué sinon).
// L'autorité (transitions, prérequis) reste l'API ; ses refus s'affichent en role="alert".

/** Actions d'état disponibles pour un état de campagne (gardes inchangées). */
export function lifecycleActionsOf(state: CampaignDetailDto['state']): {
  readonly plannable: boolean;
  readonly openable: boolean;
  readonly closeableCycle: CycleNumberDto | null;
  readonly canOpenC2: boolean;
} {
  return {
    plannable: state === 'Brouillon',
    openable: state === 'Planifiee',
    closeableCycle: state === 'OuverteC1' ? 1 : state === 'OuverteC2' ? 2 : null,
    canOpenC2: state === 'AnalyseC1',
  };
}

export function hasLifecycleAction(state: CampaignDetailDto['state']): boolean {
  const a = lifecycleActionsOf(state);
  return a.plannable || a.openable || a.closeableCycle !== null || a.canOpenC2;
}

export function LifecycleActionsPanel({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element | null {
  const actions = lifecycleActionsOf(campaign.state);
  if (!hasLifecycleAction(campaign.state)) {
    return null;
  }
  return (
    <SideCard headingId="detail-actions-heading" label="Actions">
      {actions.plannable ? <PlanAction campaign={campaign} /> : null}
      {actions.openable ? <OpenAction campaign={campaign} /> : null}
      {actions.closeableCycle !== null ? (
        <EarlyCloseAction campaign={campaign} cycleNumber={actions.closeableCycle} />
      ) : null}
      {actions.canOpenC2 ? <OpenC2Action campaign={campaign} /> : null}
    </SideCard>
  );
}

function ActionError({ message }: { readonly message: string }): React.JSX.Element {
  return (
    <p role="alert" className="text-sm text-destructive">
      {message}
    </p>
  );
}

/** Planification (« Planifier le paramétrage », E-3b-1) — Brouillon → Planifiee. */
function PlanAction({ campaign }: { readonly campaign: CampaignDetailDto }): React.JSX.Element {
  const plan = usePlanCampaign();
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Périmètre, planning des cycles et Prix Cibles définis : validez le paramétrage. La campagne
        passe en « Planifiée » et le lancement devient disponible. Aucune invitation n&apos;est envoyée
        à cette étape.
      </p>
      <Button
        type="button"
        variant="outline"
        className={DARK_BUTTON}
        onClick={() => plan.mutate(campaign.campaignId)}
        disabled={plan.isPending}
      >
        {plan.isPending ? 'Planification…' : 'Planifier le paramétrage'}
      </Button>
      {plan.isError ? <ActionError message={plan.error.message} /> : null}
    </div>
  );
}

/** Ouverture (« Lancement », UC-A-046) — Planifiee → OuverteC1, confirmation inline. */
function OpenAction({ campaign }: { readonly campaign: CampaignDetailDto }): React.JSX.Element {
  const open = useOpenCampaign();
  const [confirming, setConfirming] = React.useState(false);

  const onOpen = React.useCallback((): void => {
    open.mutate(campaign.campaignId, { onSuccess: () => setConfirming(false) });
  }, [open, campaign.campaignId]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Le lancement ouvre le Cycle 1 (passage en « Ouverte ») et déclenche l&apos;envoi des invitations
        aux fournisseurs. Aucune donnée n&apos;est consultable avant l&apos;ouverture officielle.
      </p>
      {!confirming ? (
        <Button
          type="button"
          variant="outline"
          className={DARK_BUTTON}
          onClick={() => setConfirming(true)}
          disabled={open.isPending}
        >
          Lancer la campagne
        </Button>
      ) : (
        <div
          className="space-y-3 rounded-md border border-border bg-muted/30 p-4"
          role="group"
          aria-label="Confirmation du lancement de la campagne"
        >
          <p className="text-sm">
            Confirmez-vous le lancement ? Cette action ouvre le Cycle 1 et envoie les invitations. Elle
            est irréversible.
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" onClick={onOpen} disabled={open.isPending}>
              {open.isPending ? 'Lancement…' : 'Confirmer le lancement'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setConfirming(false)}
              disabled={open.isPending}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
      {open.isError ? <ActionError message={open.error.message} /> : null}
    </div>
  );
}

/** Clôture anticipée d'un cycle (EARLY-CLOSE, A-15, UC-A-049 reframé) — G0-S15-2. */
function EarlyCloseAction({
  campaign,
  cycleNumber,
}: {
  readonly campaign: CampaignDetailDto;
  readonly cycleNumber: CycleNumberDto;
}): React.JSX.Element {
  const close = useCloseCycle();
  const [confirming, setConfirming] = React.useState(false);
  const label = cycleNumberLabel(cycleNumber);

  const onClose = React.useCallback((): void => {
    close.mutate(
      { campaignId: campaign.campaignId, body: { cycleNumber } },
      { onSuccess: () => setConfirming(false) },
    );
  }, [close, campaign.campaignId, cycleNumber]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Optionnel : ferme le {label} avant sa date de fermeture planifiée, verrouille les soumissions et
        bascule la campagne en analyse. À défaut, le cycle se clôture automatiquement à sa date de
        fermeture.
      </p>
      <Button
        type="button"
        variant="outline"
        className={DARK_BUTTON}
        onClick={() => setConfirming(true)}
        disabled={close.isPending}
      >
        {`Clôturer le ${label.toLowerCase()}`}
      </Button>
      {close.isError ? <ActionError message={close.error.message} /> : null}
      <ConfirmDialog
        open={confirming}
        title={`Clôturer le ${label} ?`}
        description={`La clôture ferme le ${label} de manière anticipée, verrouille les soumissions et fait passer la campagne en analyse. Confirmez-vous cette clôture ?`}
        confirmLabel={close.isPending ? 'Clôture…' : 'Confirmer la clôture'}
        confirmDisabled={close.isPending}
        onConfirm={onClose}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

/** Ouverture du Cycle 2 (A-17, UC-A-050) — CONDITIONNÉE à AnalyseC1 (G0-S15-4). */
function OpenC2Action({ campaign }: { readonly campaign: CampaignDetailDto }): React.JSX.Element {
  const openC2 = useOpenCampaignC2();
  const [confirming, setConfirming] = React.useState(false);

  const onOpenC2 = React.useCallback((): void => {
    openC2.mutate(campaign.campaignId, { onSuccess: () => setConfirming(false) });
  }, [openC2, campaign.campaignId]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        L&apos;analyse du Cycle 1 étant clôturée, vous pouvez ouvrir le Cycle 2 de négociation. Prérequis
        vérifiés par l&apos;API : cohorte de fournisseurs, grille de profils, Prix Cible validé et Cycle 2
        planifié.
      </p>
      <Button
        type="button"
        variant="outline"
        className={DARK_BUTTON}
        onClick={() => setConfirming(true)}
        disabled={openC2.isPending}
      >
        Ouvrir le Cycle 2
      </Button>
      {openC2.isError ? <ActionError message={openC2.error.message} /> : null}
      <ConfirmDialog
        open={confirming}
        title="Ouvrir le Cycle 2 ?"
        description="L'ouverture du Cycle 2 lance la phase de négociation et notifie les fournisseurs concernés. Confirmez-vous l'ouverture ?"
        confirmLabel={openC2.isPending ? 'Ouverture…' : "Confirmer l'ouverture"}
        confirmDisabled={openC2.isPending}
        onConfirm={onOpenC2}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}
